import React, {FunctionComponent, useCallback, useEffect, useState} from 'react';
import {Box, Card, CircularProgress, Dialog as MuiDialog, DialogActions, DialogContent, DialogTitle, IconButton, Skeleton, Tooltip, Typography} from '@mui/material';
import Button from '../../../../../../../components/base/button/button';
import Form, {MuiRFF} from '../../../../../../../components/form';
import Grid2 from '@mui/material/Unstable_Grid2';
import {DeleteOutlined, Edit, FileDownloadOutlined, Star, StarBorder} from '@mui/icons-material';
import {setReduxDialogState} from '../../../../../../../redux/slices/dialogs/actions';
import ReduxStoreRoutes from '../../../../../../../../core/models/static/routes/redux-store-routes';
import {useDispatch} from 'react-redux';
import * as Yup from 'yup';
import ValidateMessages from '../../../../../../../../core/models/static/texts/vallidate-messages';
import useIsMounted from '../../../../../../../hooks/use-is-mounted';
import {DashboardEntry, DashboardWidgetEntry} from "../../types";
import SaveOrRollback from "../../../../../../../components/app-specific/save-or-rollback";
import axios, {CancelToken} from "axios";
import useAccessLevels from "../../../../../../../hooks/use-access-levels";
import Api from "../../../../../../../../core/services/api/api";

const formNames = {
    name: 'name'
};

const schema = Yup.object().shape({
    [formNames.name]: Yup.string().trim().required(ValidateMessages.required)
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);


interface ManageDashboardsDialogProps {
    open: boolean;
    onClose: (dashboards?: DashboardEntry[]) => void;
    onExportDashboard: (dashboard: DashboardEntry) => void;
    onCreateDashboard: () => void;
    onImportDashboard: () => void;
}

const ManageDashboardsDialog = ({open, onClose, onExportDashboard, onCreateDashboard, onImportDashboard}: ManageDashboardsDialogProps) => {
    const operatorAccessLevels = useAccessLevels();
    const canOwnConfig = Boolean(operatorAccessLevels.packages.dashboard.viewDashboards && operatorAccessLevels.packages.dashboard.manageOwnDashboardConfigs);
    const canManageDashboards = Boolean(operatorAccessLevels.packages.dashboard.manageDashboards);
    const [dashboards, setDashboards] = useState<DashboardEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [togglingIsDefaultId, setTogglingIsDefaultId] = useState<number>();
    const isMounted = useIsMounted();
    const dispatch = useDispatch();

    /**
     * Fetches all dashboard templates from the server
     */
    const getDashboards = useCallback(async (cancelToken: CancelToken) => {
        setLoading(true);
        const response = await Api.listCustomDashboards(cancelToken);
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setDashboards(response.data?.map(e => ({
                ...e,
                newWidgets: e.newWidgets?.map(e => ({
                    ...e.widget,
                    connectionId: e.id,
                    customTitle: e.customTitle,
                    customFilter: e.customFilter
                }) satisfies DashboardWidgetEntry) ?? [],
            })) ?? []);
        }
    }, [isMounted]);

    /**
     * Fetches dashboards when dialog opens
     */
    useEffect(() => {
        if (!open)
            return;
        const cancelTokenSource = axios.CancelToken.source();
        void getDashboards(cancelTokenSource.token);
        return () => cancelTokenSource.cancel();
    }, [getDashboards, open]);


    /**
     * Updates an existing dashboard's name
     */
    const updateDashboard = useCallback(async (id: number, name: string) => {
        const response = await Api.updateCustomDashboard({
            id: id,
            title: name,
        });
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setDashboards(prevState => prevState.map(dashboard => dashboard.id === id ? {...dashboard, title: name} : dashboard));
        }
        return response?.resultFlag;
    }, [isMounted]);

    /**
     * Initiates dashboard deletion with confirmation
     */
    const deleteDashboard = useCallback(async (id: number) => {
        const dashboard = dashboards.find(d => d.id === id);
        if (!dashboard) return;
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            data: null,
            removedEntryTitle: `dashboard template: "${dashboard.title}"`,
            onClose: async (confirmed) => {
                if (!confirmed)
                    return true;
                const response = await Api.deleteCustomDashboard(id);
                if (!isMounted())
                    return true;
                if (response?.resultFlag) {
                    setDashboards(prevState => prevState.filter(d => d.id !== id));
                }
                return response?.resultFlag ?? false;
            }
        }));
    }, [dispatch, dashboards, isMounted]);

    /**
     * Sets a dashboard as the default template
     */
    const setDefaultDashboard = useCallback(async (id: number) => {
        setTogglingIsDefaultId(id);
        const response = await Api.setDefaultCustomDashboard({
            dashboardId: id
        });
        if (!isMounted())
            return;
        setTogglingIsDefaultId(undefined);
        if (response?.resultFlag) {
            setDashboards(prevState => prevState.map(dashboard => ({...dashboard, isDefault: dashboard.id === id})));
        }
    }, [isMounted]);

    /**
     * Handles dialog close and returns updated dashboards if not loading
     */
    const closeDialog = useCallback(() => {
        const data = loading ? undefined : dashboards;
        onClose(data);
    }, [loading, onClose, dashboards]);

    return (
        <MuiDialog
            open={open}
            onClose={closeDialog}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>Manage Dashboards</DialogTitle>
            <DialogContent dividers sx={{paddingBlock: 4}}>
                {
                    loading &&
                    Array.from({length: 4}).map((_, index) => (
                        <Skeleton
                            key={index}
                            sx={{transform: 'none', marginBlockEnd: 2}}
                            width="100%"
                            height={68}
                        />
                    ))
                }
                {
                    !loading &&
                    <>
                        {
                            dashboards.map((dashboard) => (
                                <DashboardTemplateCard
                                    key={dashboard.id}
                                    data={dashboard}
                                    onEdit={updateDashboard}
                                    onDelete={deleteDashboard}
                                    onSetDefault={setDefaultDashboard}
                                    togglingIsDefaultId={togglingIsDefaultId}
                                    onExport={onExportDashboard}
                                    canSetDefault={canOwnConfig}
                                    canManageDashboard={canManageDashboards && dashboard.isOwnedByMe}
                                />
                            ))
                        }
                        {
                            dashboards.length === 0 &&
                            <Typography variant="body2" color="textSecondary" align="center" marginBlock={10}>
                                No dashboards found
                            </Typography>
                        }
                    </>
                }
            </DialogContent>
            <DialogActions className={'d-flex align-items-center justify-content-between'} sx={{padding: 0}}>
                <Button
                    disabled={!open}
                    variant={'dialog'}
                    color={'primary'}
                    onClick={closeDialog}
                    borderRadius={'0 8px 0 8px'}
                >
                    Close
                </Button>
                {
                    canManageDashboards &&
                    <Box display="flex">
                        <Button
                            variant={"dialog"}
                            onClick={onImportDashboard}
                            borderRadius={'8px 0 0 0'}
                        >
                            Import
                        </Button>
                        <Button
                            variant={'dialog'}
                            color="primary"
                            onClick={onCreateDashboard}
                            borderRadius={'0 0 8px 0'}
                        >
                            Create
                        </Button>
                    </Box>
                }
            </DialogActions>
        </MuiDialog>
    );
};

export default ManageDashboardsDialog;

interface DashboardTemplateCardProps {
    data: DashboardEntry;
    onEdit: (id: number, name: string) => Promise<boolean | void>;
    onDelete: (id: number) => Promise<void>;
    onSetDefault: (id: number) => Promise<void>;
    togglingIsDefaultId?: number;
    onExport: (dashboard: DashboardEntry) => void;
    canSetDefault: boolean;
    canManageDashboard: boolean;
}

const DashboardTemplateCard: FunctionComponent<DashboardTemplateCardProps> = ({
                                                                                  data,
                                                                                  onEdit,
                                                                                  onDelete,
                                                                                  onSetDefault,
                                                                                  togglingIsDefaultId,
                                                                                  onExport,
                                                                                  canSetDefault,
                                                                                  canManageDashboard,
                                                                              }) => {
    const [editMode, setEditMode] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [editing, setEditing] = useState(false);
    const isMounted = useIsMounted();

    /**
     * Handles saving the edited dashboard name
     * @param values - Form values
     */
    const handleSave = useCallback(async (values: any) => {
        setEditing(true);
        const resultFlag = await onEdit(data.id, values.name);
        if (!isMounted())
            return;
        setEditing(false);
        if (resultFlag) {
            setEditMode(false);
        }
    }, [data.id, onEdit, isMounted]);

    /**
     * Reverts the form values to the original dashboard name
     * @param form
     */
    const revertValues = useCallback((form: any) => {
        form.change(formNames.name, data.title);
        form.resetFieldState(formNames.name);
        setEditMode(false);
    }, [data.title]);

    /**
     * Handles deleting the dashboard template
     */
    const handleDelete = useCallback(async () => {
        setRemoving(true);
        await onDelete(data.id);
        if (!isMounted())
            return;
        setRemoving(false);
    }, [data.id, onDelete, isMounted]);

    return (
        <Card variant={'outlined'} sx={{padding: 2, marginBlockEnd: 2}}>
            {
                editMode ? (
                    <Form onSubmit={handleSave} validate={validate} initialValues={{[formNames.name]: data.title}}>
                        {({values, form}) => (
                            <Grid2 container spacing={2} alignItems="center">
                                <Grid2 xs>
                                    <MuiRFF.TextField
                                        name={formNames.name}
                                        label="Dashboard Name"
                                        required={required[formNames.name]}
                                        fullWidth
                                    />
                                </Grid2>
                                <Grid2>
                                    <SaveOrRollback
                                        column={false}
                                        show={true}
                                        loading={editing}
                                        onRollback={() => revertValues(form)}
                                        onSave={() => handleSave(values)}
                                    />
                                </Grid2>
                            </Grid2>
                        )}
                    </Form>
                ) : (
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="body2">
                            {data.title}
                        </Typography>
                        <Box>
                            {
                                canSetDefault &&
                                <Tooltip title={data.isDefault ? "Default Dashboard" : "Set as Default"}>
                                    <Box component="span">
                                        <IconButton
                                            onClick={() => !data.isDefault ? onSetDefault(data.id) : void 0}
                                            color="primary"
                                            disabled={!!togglingIsDefaultId || removing}
                                        >
                                            {
                                                togglingIsDefaultId === data.id
                                                    ? <CircularProgress size={20}/>
                                                    : data.isDefault ? <Star/> : <StarBorder/>
                                            }
                                        </IconButton>
                                    </Box>
                                </Tooltip>
                            }
                            {
                                data.isDefault && !canSetDefault &&
                                <Tooltip title={"Default Dashboard"}>
                                    <Box component="span">
                                        <IconButton color="primary" disabled>
                                            <Star/>
                                        </IconButton>
                                    </Box>
                                </Tooltip>
                            }
                            {
                                canManageDashboard &&
                                <Tooltip title={"Export Dashboard"}>
                                    <Box component="span">
                                        <IconButton onClick={() => onExport(data)} color="primary" disabled={removing || !!togglingIsDefaultId}>
                                            <FileDownloadOutlined/>
                                        </IconButton>
                                    </Box>
                                </Tooltip>
                            }
                            {
                                canManageDashboard &&
                                <Tooltip title={editing ? "Saving..." : "Edit Dashboard"}>
                                    <Box component="span">
                                        <IconButton onClick={() => setEditMode(true)} color="primary" disabled={removing || !!togglingIsDefaultId}>
                                            {
                                                editing
                                                    ? <CircularProgress size={20}/>
                                                    : <Edit/>
                                            }
                                        </IconButton>
                                    </Box>
                                </Tooltip>
                            }
                            {
                                canManageDashboard &&
                                <Tooltip title={removing ? "Removing..." : "Delete Dashboard"}>
                                    <Box component="span">
                                        <IconButton onClick={handleDelete} color="error" disabled={removing}>
                                            {
                                                removing
                                                    ? <CircularProgress size={20}/>
                                                    : <DeleteOutlined/>
                                            }
                                        </IconButton>
                                    </Box>
                                </Tooltip>
                            }
                        </Box>
                    </Box>
                )}
        </Card>
    );
};
